/**
 * Sustained peak of |value| over a time series: the maximum, across the
 * series, of the median |value| within a forward time window. Used for
 * headline peak statistics (max lean) where a 1-2 sample excursion must not
 * claim the peak — only values the vehicle actually held.
 *
 * Degrades gracefully on sparse data: when a window holds a single sample
 * the median IS that sample, so the result approaches the instantaneous max
 * rather than dropping to zero.
 *
 * @param values - Sample values (sign is ignored; |value| is used)
 * @param timesMs - Sample times in ms (any epoch, ascending), parallel array
 * @param windowMs - Forward window length in ms
 * @returns The sustained peak of |value| (0 for an empty series)
 */
export function sustainedPeakAbs(
  values: number[],
  timesMs: number[],
  windowMs: number
): number {
  const n = values.length
  if (n === 0) return 0

  let peak = 0
  let end = 0
  // Tracks the most recent NON-end-truncated window's median, so an
  // end-truncated trailing window (cut short by running out of data, not by
  // sparsity) can fall back to it instead of computing its own median over
  // 1-2 samples — with 2 samples, floor(len/2) selects the UPPER value, so
  // a single noise spike near the end of a full-rate stream would otherwise
  // claim the peak.
  //
  // The discriminator is end-truncation, NOT "span reached windowMs": the
  // advance loop caps spans at <= windowMs, so a span-equality test only
  // fires when dt tiles the window exactly — on non-tiling rates (e.g.
  // 150 ms dt) it would never fire (no protection at all), and on streams
  // with a mid-trace dt gap, dense INTERIOR windows straddling the gap
  // would be misclassified and reuse a stale median (under-reporting).
  let lastUntruncatedMedian: number | null = null
  for (let i = 0; i < n; i++) {
    if (end < i) end = i
    while (end + 1 < n && timesMs[end + 1] - timesMs[i] <= windowMs) end++

    // End-truncated: the window ran out of data before covering windowMs.
    // (A window ending on the last sample whose span still reaches windowMs
    // is a legitimate full window, not truncated.)
    const endTruncated = end === n - 1 && timesMs[end] - timesMs[i] < windowMs

    let medianAbs: number
    if (!endTruncated || lastUntruncatedMedian === null) {
      // Interior windows always compute their own median. End-truncated
      // windows only do so when NO untruncated window exists (sparse or
      // very short traces), preserving the degrade-to-instantaneous
      // semantics documented above.
      const window: number[] = []
      for (let j = i; j <= end; j++) window.push(Math.abs(values[j]))
      window.sort((a, b) => a - b)
      medianAbs = window[Math.floor(window.length / 2)]
      if (!endTruncated) lastUntruncatedMedian = medianAbs
    } else {
      medianAbs = lastUntruncatedMedian
    }

    if (medianAbs > peak) peak = medianAbs
  }
  return peak
}
